import { Hidden } from '@mui/material'
import mixpanel from 'mixpanel-browser'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
// import { BuyerView } from 'src/components/BuyersGuideView/BuyerView'
// import { ContentPagination } from 'src/components/ContentPagination/ContentPagination'
import { Footer } from 'src/components/Footer/Footer'
// import { Breadcrumb } from 'src/components/global/Breadcrumb'
import { LoadingAnimation } from 'src/components/global/LoadingAnimation'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
// import { PremiumModal } from 'src/components/global/PremiumModal'
// import { SearchField } from 'src/components/global/SearchField'
// import { Sidebar } from 'src/components/global/Sidebar'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { IltaHeader } from 'src/components/IltaHeader/IltaHeader'
// import KnowHow from 'src/components/KnowHow/KnowHow'
import { PhasesSection } from 'src/components/PhasesSection/PhasesSection'
import { ReCaptcha } from 'src/components/ReCaptcha/ReCaptcha'
// import { SolutionView } from 'src/components/SolutionView/SolutionView'
// import { TopicCategoriesTab } from 'src/components/Tabs/TopicCategoriesTab'
// import { TopicHeader } from 'src/components/TopicHeader/TopicHeader'
// import { VendorPagination } from 'src/components/VendorPagination/VendorPagination'
import { MIXPANEL_ENABLED, THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { ISubTopicFullItemFragment, ITopicItemFragment } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption } from 'src/types'
import { getSolutionTypeFromTopic } from 'src/utils/getSolutionTypeFromTopic'
import { getTopicTabUrl } from 'src/utils/getTopicTabUrl'
import { getContentFilters, getFilters } from 'src/utils/TopicHelpers'

type Props = {
  topic: ITopicItemFragment
  subTopic: ISubTopicFullItemFragment
  filters: FilterOption[]
  contentFilters: FilterOption[]
}

const getTabIndexFromRouter = (hasContents: boolean, isPremiumCategory: boolean, tab?: string | string[]) => {
  if (isPremiumCategory && tab === 'overview') return 0
  if (tab === 'directory') return 1
  if (hasContents && tab === 'content') return 2
  return isPremiumCategory ? 0 : 1
}

const PremiumModal = dynamic(() => import('src/components/global/PremiumModal').then((mod) => mod.PremiumModal), {
  loading: () => <p className="loading-placeholder"></p>,
})
const Breadcrumb = dynamic(() => import('src/components/global/Breadcrumb').then((mod) => mod.Breadcrumb), {
  loading: () => <p className="loading-placeholder"></p>,
})
const TopicHeader = dynamic(() => import('src/components/TopicHeader/TopicHeader').then((mod) => mod.TopicHeader), {
  loading: () => <p className="loading-placeholder"></p>,
})
const TopicCategoriesTab = dynamic(
  () => import('src/components/Tabs/TopicCategoriesTab').then((mod) => mod.TopicCategoriesTab),
  {
    loading: () => <p className="loading-placeholder"></p>,
  },
)
const SolutionView = dynamic(() => import('src/components/SolutionView/SolutionView').then((mod) => mod.SolutionView), {
  loading: () => <p className="loading-placeholder"></p>,
})
const Sidebar = dynamic(() => import('src/components/global/Sidebar').then((mod) => mod.Sidebar), {
  loading: () => <p className="loading-placeholder"></p>,
})
const SearchField = dynamic(() => import('src/components/global/SearchField').then((mod) => mod.SearchField), {
  loading: () => <p className="loading-placeholder"></p>,
})
const VendorPagination = dynamic(
  () => import('src/components/VendorPagination/VendorPagination').then((mod) => mod.VendorPagination),
  {
    loading: () => <p className="loading-placeholder"></p>,
  },
)
const ContentPagination = dynamic(
  () => import('src/components/ContentPagination/ContentPagination').then((mod) => mod.ContentPagination),
  {
    loading: () => <p className="loading-placeholder"></p>,
  },
)
const BuyerView = dynamic(() => import('src/components/BuyersGuideView/BuyerView').then((mod) => mod.BuyerView), {
  loading: () => <p className="loading-placeholder"></p>,
})
const KnowHow = dynamic(() => import('src/components/KnowHow/KnowHow').then((mod) => mod.default), {
  loading: () => <p className="loading-placeholder"></p>,
})

const SubTopicPage: NextPage<Props> = ({ topic, subTopic, filters, contentFilters }) => {
  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()
  const [showModal, setShowModal] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [vendorTotalCount, setVendorTotalCount] = useState(0)
  const { isPremium, status, isVendor } = useAuthenticated()
  const hasContents = (subTopic?.contents || []).length > 0
  const [filterHeight, setFilterHeight] = useState(false)

  // const filtersDisabled = useMemo(() => Boolean(subTopic.attributes?.isPremium && !isPremium), [isPremium, subTopic])
  const changeVendorTotalCount = (value: number) => {
    if (vendorTotalCount >= 0) setVendorTotalCount(value)
  }

  const topics = subTopic?.topics || []
  const isPremiumCategory = !!subTopic?.isPremium

  // Get search keyword from query parameters for highlighting (reactive to router changes)
  const searchKeyword = useMemo(() => {
    if (!router.isReady) return undefined
    const keyword = router.query.keyword
    if (!keyword) return undefined

    let keywordStr: string
    if (Array.isArray(keyword)) {
      keywordStr = keyword[0] || ''
    } else {
      keywordStr = keyword as string
    }

    // Decode URI component in case it's URL encoded (handles + as space, %20, etc.)
    try {
      return decodeURIComponent(keywordStr)
    } catch {
      // If decoding fails, return as is
      return keywordStr || undefined
    }
  }, [router.isReady, router.query.keyword])

  useEffect(() => {
    const newTabIndex = getTabIndexFromRouter(hasContents, isPremiumCategory, router.query.tab)
    if (isPremium && tabIndex !== newTabIndex) {
      setTabIndex(newTabIndex)
    }
    if (!isPremium && newTabIndex === 0) {
      if (status === 'loading') {
        setTabIndex(-1)
      } else {
        setTabIndex(1)
      }
    } else {
      setTabIndex(newTabIndex)
    }
  }, [router, isPremium, status])

  useEffect(() => {
    if (MIXPANEL_ENABLED) {
      mixpanel.track('visit_sub_topic', {
        subTopicId: subTopic?.documentId,
        subTopicName: subTopic?.name,
        subTopicSlug: subTopic?.slug,
        topicId: topic?.documentId,
        topicName: topic?.name,
        topicSlug: topic?.slug,
      })
    }
  }, [])

  const meta = (
    <Meta
      title={subTopic?.name || ''}
      description={subTopic?.description || ''}
      url={`/topics/${topic?.slug}/${subTopic?.slug}/`}
      keywords={(topics || [])
        .map((topic: any) => topic?.name || '')
        .concat((topics || []).map((topic) => topic?.description || ''))}
      article={{
        authors: ['Nicola (Nikki) Shaver, Jeroen Plink'],
        section: 'Product Type',
        publishedTime: subTopic?.publishedAt || '',
        modifiedTime: subTopic?.publishedAt || '',
      }}
    />
  )

  const showOverviewTab = isPremium && isPremiumCategory
  const solutionType = getSolutionTypeFromTopic(topic)

  const onSelectTab = (tabId: number) => {
    setTabIndex(tabId)
    let newTab = 'directory'
    let defaultSort = 'Default'
    if (tabId === 0) {
      newTab = 'overview'
    } else if (tabId === 1) {
      newTab = 'directory'
      defaultSort = 'Default'
    } else if (tabId === 2) {
      newTab = 'content'
      defaultSort = 'Category_Chronologically'
    }
    const keyword = router.query.keyword
    defaultSort = searchKeyword ? 'bykeyword' : defaultSort

    updateQueryParams({
      tab: newTab,
      sort: defaultSort,
      page: undefined,
      keyword: (keyword as string | undefined) || undefined,
    })
  }

  const basePath = `/topics/${topic?.slug}/${subTopic?.slug}`

  return (
    <ReCaptcha>
      <Main meta={meta}>
        <PremiumModal
          origin={`/topics/${topic?.slug}/${subTopic?.slug}/`}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />
        <MainContainer id="mainContainer">
          <ThemeOnly theme="ilta">
            <IltaHeader />
          </ThemeOnly>
          <div className="flex flex-col bg-clouds">
            <Breadcrumb subject={topic?.name} subjectRoute={`/topics/${topic?.slug}`} subtitle={subTopic?.name || ''} />
            <TopicHeader topic={subTopic} subtopicButtonFlag={true} />
            <ThemeOnly theme="legaltech">
              <div role="tablist" className="h-[50px] w-full flex flex-row justify-center">
                {showOverviewTab && (
                  <TopicCategoriesTab id={0} activeTab={tabIndex} onSelectTab={onSelectTab}>
                    OVERVIEW
                  </TopicCategoriesTab>
                )}
                <TopicCategoriesTab id={1} activeTab={tabIndex} onSelectTab={onSelectTab}>
                  DIRECTORY {vendorTotalCount > 0 ? `(${vendorTotalCount})` : ''}
                </TopicCategoriesTab>
                {hasContents && (
                  <TopicCategoriesTab id={2} activeTab={tabIndex} onSelectTab={onSelectTab}>
                    CONTENT
                  </TopicCategoriesTab>
                )}
              </div>
            </ThemeOnly>
          </div>
          <hr />
          <div className="flex flex-col">
            <div className="flex justify-center">
              {showOverviewTab && tabIndex === 0 && (
                <div className="!max-w-[1100px] flex flex-col justify-between w-full mb-32">
                  <SolutionView
                    subTopicId={subTopic.documentId || ''}
                    type={solutionType}
                    url={getTopicTabUrl(basePath, 'directory', router.query)}
                  />
                  <PhasesSection
                    topicSlug={topic?.slug}
                    subTopicSlug={subTopic?.slug || ''}
                    subTopicName={subTopic?.name || ''}
                    viewAllUrl={getTopicTabUrl(basePath, 'content', router.query)}
                  />
                  {!isVendor && (
                    <BuyerView
                      contentUrl={getTopicTabUrl(basePath, 'content', router.query)}
                      subTopicSlug={subTopic?.slug || ''}
                    />
                  )}
                  <KnowHow subTopicSlug={subTopic?.slug || ''} />
                </div>
              )}
              {tabIndex === 1 && (
                <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32 relative">
                  <Hidden mdDown>
                    <div className="w-1/4 pr-10 flex flex-col">
                      <SearchField className="my-8" />
                      <Sidebar filterOptions={filters} defaultSelectFilterIdx={1} filterHeight={filterHeight} />
                    </div>
                  </Hidden>
                  <VendorPagination
                    filterProps={filters}
                    subTopic={subTopic}
                    solutionType={solutionType}
                    changeTotalCount={changeVendorTotalCount}
                    includeCompany={false}
                    setFilterHeight={setFilterHeight}
                  />
                </div>
              )}
              {hasContents && tabIndex === 2 && (
                <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
                  <Hidden mdDown>
                    <div className="w-1/4 pr-10 flex flex-col">
                      <SearchField className="my-8" />
                      <Sidebar
                        filterOptions={contentFilters}
                        // filtersDisabled={filtersDisabled}
                        defaultSelectFilterIdx={1}
                        onClickWhenDisabled={() => setShowModal(true)}
                      />
                    </div>
                  </Hidden>
                  <ContentPagination
                    filterProps={contentFilters}
                    preAppliedFilters={{ subTopicIds: subTopic.documentId! }}
                    // filtersDisabled={filtersDisabled}
                    onClickWhenDisabled={() => setShowModal(true)}
                    sortType="category_contents"
                    searchKeyword={searchKeyword}
                  />
                </div>
              )}
              {tabIndex === -1 && (
                <div className="!max-w-[1100px] flex flex-row items-center w-full mb-32">
                  <LoadingAnimation></LoadingAnimation>
                </div>
              )}
            </div>
          </div>
          <ThemeOnly theme="legaltech">
            <Footer />
          </ThemeOnly>
        </MainContainer>
      </Main>
    </ReCaptcha>
  )
}

export default SubTopicPage

export const getStaticProps: GetStaticProps<any> = async ({ params }) => {
  if (!params?.slug) return { revalidate: 60, notFound: true }

  const slug = params.slug as string[]
  const tSlug = slug[0]
  const stSlug = slug[1]
  try {
    const [{ topics }, { subTopics }] = await Promise.all([
      apolloSdk.getTopicBySlug({ slug: tSlug }),
      apolloSdk.getSubTopicBySlug({ slug: stSlug }),
    ])

    if (!topics?.length || !subTopics?.length) return { revalidate: 60, notFound: true }

    const filters = await getFilters('subtopic', null, '', THEME)
    const contentFilters: FilterOption[] = await getContentFilters()

    return {
      props: {
        topic: topics[0],
        subTopic: subTopics[0],
        filters,
        contentFilters,
      },
      revalidate: 60,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error occurred in getStaticProps of Sub-Topics:', error)
    // Graceful fallback in case of an upstream error
    return {
      notFound: true,
      revalidate: 5, // Reattempt fetching during the next static regeneration
    }
  }
}

// static paths
export const getStaticPaths: GetStaticPaths = async () => {
  const { topics } = await apolloSdk.getTopics()
  const paths: Array<string | { params: { slug: string[] }; locale?: string }> = []
  if (topics) {
    topics.forEach((topic: any) => {
      if (!topic) return
      if (!topic?.subTopics) return

      topic?.subTopics.forEach((subTopic: any) => {
        if (!subTopic) return
        if (!topic?.slug || !subTopic?.slug) {
          return
        }
        paths.push({ params: { slug: [topic?.slug, subTopic?.slug] } })
      })
    })
  }
  return {
    paths,
    fallback: 'blocking',
  }
}
